import Behaviour, { SingleBehaviour } from "../../lib/ui/Behaviour";
import { C } from "../../lib/ui/constructors";
import { AbstractDropdownSelection } from "../ui/widgets/AbstractDropdownSelection";
import { Clues } from "../model/Clues";
import { TileCoordinates, TileRectangle } from "../../lib/runescape/coordinates";
import { capitalize } from "lodash";
import { Path } from "../../lib/runescape/pathing";
import { MethodPackManager } from "../MethodPackManager";
import BoundsBuilder from "../../lib/gamemap/BoundsBuilder";
import { RenderingUtility } from "../ui/map/RenderingUtility";
import MethodSelector from "./MethodSelector";
import PathControl from "./PathControl";
import { CursorType } from "../../lib/runescape/CursorType";
import { TileArea } from "../../lib/runescape/coordinates/TileArea";
import { ClueEntities } from "./ClueEntities";
import { NislIcon } from "../ui/nisl";
import { ClueProperties } from "../ui/theorycrafting/ClueProperties";
import { SlideGuider, SliderSolving } from "./subbehaviours/SliderSolving";
import { CompassSolving } from "./subbehaviours/compass/CompassSolving";
import { ScanTreeSolving } from "./subbehaviours/scans/ScanTreeSolving";
import { KnotSolving } from "./subbehaviours/KnotSolving";
import { LockboxSolving } from "./subbehaviours/LockboxSolving";
import { TowersSolving } from "./subbehaviours/TowersSolving";
import { Log } from "../../lib/util/Log";
import { SimpleScanSolving } from "./subbehaviours/scans/SimpleScanSolving";
import { ScanSolving } from "./subbehaviours/scans/ScanSolving";
import { Transportation } from "../../lib/runescape/transportation";
import { SettingsNormalization } from "../../lib/util/SettingsNormalization";
import { PathOverlayControl } from "../overlay3d/PathOverlayControl";
import { ClueSolvingMapLayer } from "./ClueSolvingMapLayer";
import { ClueReadingBehaviour } from "./ClueReadingBehaviour";
import { SC } from "../../lib/settings";
import { MutableMesh } from "../overlay3d/meshes/MutableMesh";
import { Alt1 } from "../../lib/alt1/Alt1";
import { drawTileArea } from "../overlay3d/PathRender";
import { SimpleGLOverlay } from "../overlay3d/SimpleGLOverlay";
import { FakeLodash } from "../../lib/coreutil/FakeLodash";
var span = C.span;
var interactionMarker = RenderingUtility.interactionMarker;
var inlineimg = C.inlineimg;
var item = C.item;
var cls = C.cls;
var vbox = C.vbox;
var bold = C.bold;
var spacer = C.spacer;
var space = C.space;
var hboxl = C.hboxl;
var activate = TileArea.activate;
var log = Log.log;
var default_interactive_area = Transportation.EntityTransportation.default_interactive_area;
var digSpotArea = Clues.digSpotArea;
/**
 * ClueSolvingBehaviour is the central coordinator for clue solving.
 * It controls the map, the {@link ClueReader}, (automatic) method selection, puzzle solvers, and more.
 *
 * Logic specific to different types of clues and puzzles is outsourced in subclasses of {@link ClueSolvingSubBehaviour}.
 */
export default class ClueSolvingBehaviour extends Behaviour {
    constructor(app, tetracompass_only) {
        super();
        this.app = app;
        this.tetracompass_only = tetracompass_only;
        // Active clue step and step history
        this.state = null;
        this.history = [];
        this.active_method = null;
        this.active_behaviour = this.withSub(new SingleBehaviour());
        // Controller for path display on the map, including an ui component
        this.path_control = this.withSub(new PathControl(this));
        // Method dropdown ui component
        this.method_selector = null;
        this.gl_overlay = this.withSub(new SingleBehaviour());
        // Initialize here because the subbehaviour requires this.tetracompass_only to be set
        this.clue_reader = this.withSub(new ClueReadingBehaviour(this));
        this.path_control.section_selected.on(p => {
            if (this.active_method?.method?.type != "scantree")
                this.map_layer.fit(Path.bounds(p));
            else {
                const behaviour = this.active_behaviour.get();
                if (behaviour instanceof ScanTreeSolving)
                    behaviour.onSectionSelectedInPathControl(p);
            }
        });
        this.overlay_control = this.withSub(new PathOverlayControl());
    }
    /**
     * Get an estimate for the player's current position based on the solution of the previous clue.
     * Currently, only scans and compasses report their respective solution to be used by this.
     * Solutions expire after 10 seconds, puzzles extend this expiry.
     */
    getAssumedPlayerPositionByLastClueSolution() {
        const DEBUG_LAST_SOLUTION_OVERRIDE = null;
        if (DEBUG_LAST_SOLUTION_OVERRIDE)
            return DEBUG_LAST_SOLUTION_OVERRIDE;
        const now = Date.now();
        const LOOKBACK = 10000; // 10 seconds
        let OLDEST_CONSIDERED_STEP = now - LOOKBACK;
        for (let i = this.history.length - 2; i >= 0; i--) {
            const state = this.history[i];
            if (state.end_time < OLDEST_CONSIDERED_STEP)
                return null;
            if (state.step.type == "puzzle")
                OLDEST_CONSIDERED_STEP = state.start_time - LOOKBACK;
            else
                return state.solution_area;
        }
        return null;
    }
    activateSubBehaviour(behaviour) {
        if (behaviour) {
            behaviour.setRelatedState(this.state);
            log().log(`Activating subbehaviour: ${behaviour.constructor.name}`, "Solving");
        }
        this.active_behaviour.set(behaviour);
    }
    pushState(state, read_result) {
        const now = Date.now();
        if (this.state) {
            this.state.read_result = undefined;
            if (!this.state.end_time) {
                this.state.end_time = now;
            }
        }
        const next_id = this.history.length > 0 ? this.history[this.history.length - 1].state_id + 1 : 0;
        this.state = {
            state_id: next_id,
            step: state,
            start_time: now,
            read_result: read_result,
            end_time: undefined,
            solution_area: undefined
        };
        log().log(`Created state ${this.state.state_id} (${ClueSolving.ClueState.title(this.state)})`, "Solving");
        this.history.push(this.state);
        return this.state;
    }
    setPuzzle(puzzle) {
        if (this.state?.step?.type == "puzzle" && this.state.step.puzzle.type == puzzle?.type)
            return false; // Don't do anything if a puzzle of that type is already active
        this.reset(this.state);
        this.pushState({ type: "puzzle", puzzle: puzzle }, undefined);
        if (puzzle) {
            this.activateSubBehaviour((() => {
                switch (puzzle.type) {
                    case "slider":
                        return new SliderSolving(this, puzzle);
                    case "knot":
                        return new KnotSolving(this, puzzle);
                    case "lockbox":
                        return new LockboxSolving(this, puzzle);
                    case "tower":
                        return new TowersSolving(this, puzzle);
                }
            })());
        }
        return true;
    }
    setSolutionArea(state, area) {
        if (!state)
            return;
        log().log(`Setting clue solution (state ${state.state_id}) to ${TileArea.toString(area)}`, "Solving");
        state.solution_area = area;
    }
    fitToClue() {
        if (!this.state.step || this.state.step.type == "puzzle")
            return;
        const step = this.state.step.clue;
        const bounds = new BoundsBuilder();
        const sol = Clues.Step.solution(step.step);
        switch (sol?.type) {
            case "talkto":
                let npc_spot_id = 0;
                let spot = sol.spots[npc_spot_id];
                bounds.addArea(spot.range);
                break;
            case "dig":
                bounds.addTile(sol.spot);
                break;
            case "search":
                bounds.addRectangle(sol.spot);
                break;
        }
        switch (step.step.type) {
            case "emote":
                bounds.addArea(step.step.area);
                break;
            case "skilling":
                bounds.addRectangle(TileArea.toRect(step.step.areas[0]));
                break;
            case "scan":
                bounds.addTile(...step.step.spots);
                break;
        }
        this.map_layer.fit(bounds.get(), "setting");
    }
    /**
     * Sets the active clue. Builds the ui elements and moves the map view to the appropriate spot.
     *
     * @param step The clue step combined with the index of the selected text variant.
     * @param fit_target
     * @param read_result
     */
    setClue(step, fit_target = true, read_result) {
        if (this.state?.step?.type == "clue" && this.state.step.clue.step.id == step.step.id && this.state.step.clue.text_index == step.text_index) {
            return;
        }
        if (this.state?.read_result && this.state?.step?.type == "clue" && this.state.step.clue.step.type == "compass" && step.step.type == "compass") {
            // This is a workaround fix for a rare issue where a master compass is reset and reloaded as an elite compass because of interface interference.
            // Changing from an active compass to another compass is disallowed.
            return;
        }
        this.reset(this.state);
        const state = this.pushState({ type: "clue", clue: step }, read_result);
        switch (step.step.solution?.type) {
            case "search":
                this.setSolutionArea(state, default_interactive_area(step.step.solution.spot));
                break;
            case "dig":
                this.setSolutionArea(state, digSpotArea(step.step.solution.spot));
                break;
            case "talkto":
                if (step.step.solution.spots.length == 1) {
                    this.setSolutionArea(state, step.step.solution.spots[0].range);
                }
                break;
        }
        if (step.step.type == "anagram") {
            step.step.solution.spots;
        }
        const settings = this.app.settings.settings.solving;
        const clue = step.step;
        // Render controls and solution on map
        {
            let w = c();
            if (step.step.type == "map") {
                if (settings.info_panel.map_image == "show") {
                    w.append(c(`<img src='${step.step.image_url}' style="width: 100%">`).addClass("ctr-neosolving-solution-row"));
                }
                else if (settings.info_panel.map_image == "transcript") {
                    cls("ctr-neosolving-solution-row").text(step.step.text[0]).appendTo(w);
                }
            }
            else {
                switch (settings.info_panel.clue_text) {
                    case "full":
                        cls("ctr-neosolving-solution-row").text(step.step.text[step.text_index]).appendTo(w);
                        break;
                    case "abridged":
                        if (clue.type == "compass")
                            break;
                        const short = Clues.Step.shortString(clue, step.text_index);
                        const row = cls("ctr-neosolving-solution-row").text(short).appendTo(w);
                        if (clue.text[step.text_index])
                            row.addClass("ctr-neosolving-solution-row-shortened");
                        break;
                    case "hide":
                    // Do nothing
                }
            }
            const sol = Clues.Step.solution(step.step);
            if (sol) {
                switch (sol?.type) {
                    case "talkto":
                        let npc_spot_id = 0;
                        let spot = sol.spots[npc_spot_id];
                        if (settings.info_panel.talk_target == "show") {
                            w.append(cls("ctr-neosolving-solution-row")
                                .append(inlineimg("/assets/icons/cursor_talk.png"), span("Talk to "), C.npc(sol.npc, true)
                                .tooltip("Click to center")
                                .on("click", () => {
                                this.map_layer.fit(TileArea.toRect(spot.range));
                            }), spot.description
                                ? span(" " + spot.description)
                                : undefined));
                        }
                        for (let i = 0; i < sol.spots.length; i++) {
                            const spot = sol.spots[i];
                            new ClueEntities.TalkSolutionNpcEntity(sol.npc, spot)
                                .addTo(this.map_layer.generic_solution_layer);
                        }
                        break;
                    case "search":
                        if (sol.key && settings.info_panel.search_key == "show") {
                            w.append(cls("ctr-neosolving-solution-row").append(inlineimg("/assets/icons/key.png"), " ", span(sol.key.answer).addClass("ctr-clickable").on("click", () => {
                                this.map_layer.fit(TileArea.toRect(sol.key.area));
                            })));
                        }
                        if (settings.info_panel.search_target == "show") {
                            w.append(cls("ctr-neosolving-solution-row").append(inlineimg("/assets/icons/cursor_search.png"), " ", span("Search "), C.staticentity(sol.entity, true)
                                .tooltip("Click to center")
                                .on("click", () => {
                                this.map_layer.fit(sol.spot);
                            })));
                        }
                        new ClueEntities.SearchSolutionEntity(sol)
                            .addTo(this.map_layer.generic_solution_layer);
                        break;
                    case "dig":
                        if (settings.info_panel.dig_target == "show") {
                            w.append(cls("ctr-neosolving-solution-row").append(inlineimg("/assets/icons/cursor_shovel.png"), " ", span("Dig"), sol.description
                                ? span(" " + sol.description)
                                : span(` at ${TileCoordinates.toString(sol.spot)}`)));
                        }
                        new ClueEntities.DigSolutionEntity(sol)
                            .addTo(this.map_layer.generic_solution_layer);
                        break;
                }
            }
            if (clue.type == "emote") {
                if (clue.hidey_hole && settings.info_panel.hidey_hole == "show") {
                    w.append(cls("ctr-neosolving-solution-row").append(inlineimg("/assets/icons/cursor_search.png"), " ", span("Get items from "), C.staticentity(clue.hidey_hole.name)
                        .tooltip("Click to center")
                        .addClass("ctr-clickable")
                        .on("click", () => {
                        this.map_layer.fit(clue.hidey_hole.location);
                    })));
                }
                if (settings.info_panel.emote_items == "show") {
                    let row = cls("ctr-neosolving-solution-row").append(inlineimg("/assets/icons/cursor_equip.png"), " ", span("Equip ")).appendTo(w);
                    for (let i = 0; i < clue.items.length; i++) {
                        const itm = clue.items[i];
                        if (i > 0) {
                            if (i == clue.items.length - 1)
                                row.append(", and ");
                            else
                                row.append(", ");
                        }
                        row.append(item(itm));
                    }
                }
                if (settings.info_panel.emotes == "show") {
                    let row = cls("ctr-neosolving-solution-row").append(inlineimg("/assets/icons/emotes.png"), " ").appendTo(w);
                    for (let i = 0; i < clue.emotes.length; i++) {
                        const item = clue.emotes[i];
                        if (i > 0) {
                            if (i == clue.emotes.length - 1)
                                row.append(", then ");
                            else
                                row.append(", ");
                        }
                        row.append(item).addClass("nisl-emote");
                    }
                }
                if (clue.double_agent && settings.info_panel.double_agent == "show") {
                    w.append(cls("ctr-neosolving-solution-row").append(inlineimg("/assets/icons/cursor_attack.png"), space(), span("Kill "), C.npc("Double Agent")));
                }
                new ClueEntities.EmoteAreaEntity(clue).addTo(this.map_layer.generic_solution_layer);
                if (clue.hidey_hole) {
                    new ClueEntities.HideyHoleEntity(clue, false).addTo(this.map_layer.generic_solution_layer);
                }
            }
            else if (clue.type == "skilling") {
                w.append(cls("ctr-neosolving-solution-row").append(inlineimg(CursorType.meta(clue.cursor).icon_url), space(), span(clue.answer)));
                interactionMarker(activate(clue.areas[0]).center(), clue.cursor)
                    .addTo(this.map_layer.generic_solution_layer);
            }
            else if (clue.type == "scan") {
                this.map_layer.scan_layer.marker.setClickable(true);
                this.map_layer.scan_layer.setSpots(clue.spots);
                this.map_layer.scan_layer.marker.setRadius(clue.range + 5, true);
            }
            else if (clue.type == "compass") {
                // bounds.addTile(...clue.spots)
            }
            if (clue.challenge && clue.challenge.length > 0) {
                const challenge = clue.challenge.find(c => c.type == "challengescroll");
                if (challenge) {
                    const answer_id = this.app.favourites.getChallengeAnswerId(clue);
                    const answer = challenge.answers[answer_id];
                    if (settings.info_panel.challenge != "hide") {
                        let row = null;
                        let answer_span = null;
                        w.append(row = cls("ctr-neosolving-solution-row")
                            .toggleClass("ctr-clickable", challenge.answers.length > 1)
                            .append(hboxl(inlineimg("/assets/icons/activeclue.png"), vbox(settings.info_panel.challenge == "full"
                            ? c().css("font-style", "italic").text(challenge.question) : undefined, hboxl(bold(`Answer:`), space(), answer_span = span(answer.answer.toString()), spacer(), challenge.answers.length > 1 ? NislIcon.dropdown() : undefined)).css("flex-grow", "1"))));
                        if (challenge.answers.length > 1) {
                            row.on("click", () => {
                                new AbstractDropdownSelection.DropDown({
                                    dropdownClass: "ctr-neosolving-favourite-dropdown",
                                    renderItem: a => c().text(`${a.answer} (${a.note})`)
                                })
                                    .setItems(challenge.answers)
                                    .setHighlighted(answer)
                                    .open(row, undefined)
                                    .onSelected(a => {
                                    this.app.favourites.setChallengeAnswerId(clue, challenge.answers.indexOf(a));
                                    answer_span.text(a.answer.toString());
                                });
                            });
                        }
                    }
                }
                else {
                    if (settings.info_panel.puzzle == "show" || (settings.info_panel.puzzle == "hideforscansandcompasses" && step.step.type != "compass" && step.step.type != "scan")) {
                        const row = cls("ctr-neosolving-solution-row").appendTo(w);
                        for (let i = 0; i < clue.challenge.length; i++) {
                            if (i > 0) {
                                if (i == clue.challenge.length - 1)
                                    row.append(", or ");
                                else
                                    row.append(", ");
                            }
                            row.append(ClueProperties.render_challenge(clue.challenge[i]).css("display", "inline-block"));
                        }
                    }
                }
            }
            if (!w.container.is(":empty"))
                this.map_layer.solution_container.append(w);
        }
        if (fit_target)
            this.fitToClue();
        if (this.app.settings.settings.teleport_customization.preset_bindings_active) {
            const active_preset = this.app.settings.settings.teleport_customization.active_preset;
            const bound_preset = this.app.settings.settings.teleport_customization.preset_bindings[clue.tier];
            if (active_preset != bound_preset && bound_preset != null)
                this.app.settings.update(set => set.teleport_customization.active_preset = bound_preset);
        }
        if (clue.type == "compass") {
            const behaviour = new CompassSolving(this, clue, read_result?.type == "compass" ? read_result.capture : undefined, async (spot) => {
                if (spot) {
                    const method = await this.getAutomaticMethod({ clue: clue.id, spot: spot });
                    this.setMethod(method);
                }
                else {
                    this.setMethod(null);
                }
            });
            if (!read_result)
                this.map_layer.fit(TileRectangle.from(...clue.spots));
            this.activateSubBehaviour(behaviour);
        }
        // Render gl overlays
        if (Alt1.instance().featureGL()) {
            (async () => {
                const builder = new MutableMesh();
                switch (step.step.type) {
                    case "emote":
                        builder.add((await drawTileArea(step.step.area))
                            .recolor([255, 0, 255, 255]));
                        break;
                }
                const solution = Clues.Step.solution(step.step);
                if (solution) {
                    switch (solution.type) {
                        case "dig":
                            builder.add((await drawTileArea(digSpotArea(solution.spot))).recolor([100, 100, 100, 255]));
                            break;
                        case "search":
                            builder.add((await drawTileArea(TileArea.fromRect(solution.spot))).recolor([0, 255, 255, 255]).translate({ x: 0, y: 0.25, z: 0 }), (await drawTileArea(TileArea.fromRect(solution.spot))).recolor([0, 255, 255, 255]).translate({ x: 0, y: 0.75, z: 0 }));
                            break;
                        case "talkto":
                            for (let spot of solution.spots) {
                                builder.add((await drawTileArea(spot.range)).recolor([255, 255, 0, 255]).translate({ x: 0, y: 0.5, z: 0 }), (await drawTileArea(spot.range)).recolor([255, 255, 0, 255]).translate({ x: 0, y: 1.0, z: 0 }), (await drawTileArea(spot.range)).recolor([255, 255, 0, 255]).translate({ x: 0, y: 1.5, z: 0 }));
                            }
                            break;
                    }
                }
                return builder.finalize();
            })().then(mesh => {
                if (state != this.state)
                    return;
                this.gl_overlay.set(new SimpleGLOverlay(mesh));
            });
        }
    }
    /**
     * Sets the active solving method.
     * Use {@link setClue} to activate the related clue first!
     * Builds the necessary ui elements and potentially zooms to the start point.
     *
     * @param method
     * @param read_result
     */
    async setMethod(method, read_result = null) {
        log().log(`Setting method ${method ? method.method.name : "null"}`);
        if (!read_result && this.state?.read_result)
            read_result = this.state.read_result;
        if (method) {
            log().log(`Setting method to ${method.method.name} (${method.method.id.substring(0, 8)})`, "Solving");
        }
        else {
            log().log(`Setting method to null`, "Solving");
        }
        if (this.state?.step?.type != "clue") {
            log().log(`Aborting set method because active state is not a clue step: '${this.state?.step?.type}'.`, "Solving");
            return;
        }
        if (method && (method.clue.clue.id != this.state?.step?.clue?.step?.id)) {
            log().log(`Aborting set method because method does not match clue.`, "Solving");
            return;
        }
        if (method && method == this.active_method) {
            log().log(`Aborting set method because same method is already active.`, "Solving");
            return;
        }
        this.path_control.reset();
        this.method_selector.hide();
        this.active_method = null;
        const clue = this.state.step.clue.step;
        const active_behaviour = this.active_behaviour.get();
        if (active_behaviour?.type == "method") {
            active_behaviour.stop();
        }
        if (method) {
            this.active_method = method;
            if (method.method.type == "scantree") {
                const scan_interface = (() => {
                    if (read_result?.type == "scan") {
                        return read_result.scan_interface;
                    }
                    else
                        return null;
                })();
                this.activateSubBehaviour(new ScanTreeSolving(this, method, scan_interface, this.app.settings.observable.scans));
            }
            else if (method.method.type == "general_path") {
                this.path_control.setMethod(method);
                this.overlay_control.setIngameOverlays([
                    method.method.pre_path,
                    method.method.main_path,
                    method.method.post_path,
                ]);
            }
        }
        else {
            if (clue.type == "scan") {
                // When deselecting the active scan tree, zoom to the spots
                if (active_behaviour instanceof ScanTreeSolving)
                    this.map_layer.fit(TileRectangle.from(...clue.spots));
                const behaviour = new SimpleScanSolving(this, clue, read_result?.type == "scan" ? read_result.scan_interface : null);
                this.activateSubBehaviour(behaviour);
            }
        }
        if ((this.state.step?.clue?.step?.type != "compass") || (active_behaviour instanceof CompassSolving && active_behaviour.selected_spot.value())) {
            const clue = active_behaviour instanceof CompassSolving
                ? { clue: this.state.step.clue.step.id, spot: active_behaviour.selected_spot.value().spot.spot }
                : { clue: this.state.step.clue.step.id };
            this.method_selector.setClue(clue, method)
                .then(() => this.method_selector.show());
        }
    }
    async getAutomaticMethod(step) {
        let m = await this.app.favourites.getMethod(step);
        if (m === undefined) {
            let ms = await MethodPackManager.instance().getForClue(step);
            if (ms.length > 0)
                m = ms[0];
        }
        return m;
    }
    async setClueWithAutomaticMethod(step, read_result) {
        if (this.state?.step?.type == "clue" && this.state.step.clue.step.id == step.step.id && this.state.step.clue.text_index == step.text_index) {
            return;
        }
        let m = await this.getAutomaticMethod({ clue: step.step.id });
        this.setClue(step, !m, read_result);
        this.setMethod(m, read_result);
    }
    /**
     * Resets both the active clue and method, resets all displayed pathing.
     */
    reset(state) {
        if (state != this.state)
            return false; // The caller needs to provide the active state as a token to prevent potential timing issues
        this.map_layer.reset();
        this.path_control.reset();
        this.activateSubBehaviour(null);
        this.overlay_control.reset();
        this.method_selector.hide();
        this.state = null;
        this.active_method = null;
        this.gl_overlay.set(null);
        log().log("Reset state", "Solving");
        return true;
    }
    begin() {
        this.app.map.addGameLayer(this.map_layer = new ClueSolvingMapLayer(this));
        this.method_selector = new MethodSelector(this.app.favourites)
            .addClass("ctr-neosolving-solution-row")
            .appendTo(this.map_layer.method_selection_container);
        this.method_selector.method_selected.on(m => {
            this.app.favourites.setMethod(m.clue, m.method);
            this.setMethod(m.method);
            if (!m)
                this.fitToClue();
        });
    }
    end() {
        this.map_layer.remove();
    }
}
export var ClueSolving;
(function (ClueSolving) {
    let ClueState;
    (function (ClueState) {
        function title(state) {
            switch (state.step.type) {
                case "puzzle":
                    switch (state.step.puzzle.type) {
                        case "slider":
                            return `Slider (${state.step.puzzle.puzzle.theme})`;
                        case "knot":
                            return "Knot";
                        case "tower":
                            return "Tower";
                        case "lockbox":
                            return "Lockbox";
                        default:
                            return "Unknown Puzzle";
                    }
                case "clue":
                    return `${capitalize(state.step.clue.step.type)} id ${state.step.clue.step.id}`;
            }
        }
        ClueState.title = title;
    })(ClueState = ClueSolving.ClueState || (ClueSolving.ClueState = {}));
    let Settings;
    (function (Settings) {
        let Puzzles;
        (function (Puzzles) {
            Puzzles.DEFAULT = {
                sliders: SlideGuider.Settings.DEFAULT,
                knots: KnotSolving.Settings.DEFAULT,
                lockboxes: LockboxSolving.Settings.DEFAULT,
                towers: TowersSolving.Settings.DEFAULT,
            };
            function normalize(settings) {
                if (!settings)
                    return FakeLodash.cloneDeep(Puzzles.DEFAULT);
                settings.sliders = SlideGuider.Settings.normalize(settings.sliders);
                settings.knots = KnotSolving.Settings.normalize(settings.knots);
                settings.lockboxes = LockboxSolving.Settings.normalize(settings.lockboxes);
                settings.towers = TowersSolving.Settings.normalize(settings.towers);
                return settings;
            }
            Puzzles.normalize = normalize;
        })(Puzzles = Settings.Puzzles || (Settings.Puzzles = {}));
        let InfoPanel;
        (function (InfoPanel) {
            InfoPanel.EVERYTHING = {
                clue_text: "full",
                map_image: "show",
                dig_target: "show",
                talk_target: "show",
                search_target: "show",
                search_key: "show",
                hidey_hole: "show",
                emote_items: "show",
                emotes: "show",
                double_agent: "show",
                challenge: "full",
                puzzle: "show",
                path_step_list: "show",
            };
            InfoPanel.NOTHING = {
                clue_text: "hide",
                map_image: "hide",
                dig_target: "hide",
                talk_target: "hide",
                search_target: "hide",
                search_key: "hide",
                hidey_hole: "hide",
                emote_items: "hide",
                emotes: "hide",
                double_agent: "hide",
                challenge: "hide",
                puzzle: "hide",
                path_step_list: "hide",
            };
            InfoPanel.REDUCED = {
                clue_text: "abridged",
                map_image: "show",
                dig_target: "show",
                talk_target: "show",
                search_target: "show",
                search_key: "hide",
                hidey_hole: "hide",
                emote_items: "hide",
                emotes: "hide",
                double_agent: "hide",
                challenge: "answer_only",
                puzzle: "hideforscansandcompasses",
                path_step_list: "hide",
            };
            InfoPanel.DEFAULT = InfoPanel.REDUCED;
            function normalize(settings) {
                if (!settings)
                    return FakeLodash.cloneDeep(InfoPanel.DEFAULT);
                if (!["full", "hide", "abridged"].includes(settings.clue_text))
                    settings.clue_text = InfoPanel.DEFAULT.clue_text;
                if (!["show", "hide", "transcript"].includes(settings.map_image))
                    settings.map_image = InfoPanel.DEFAULT.map_image;
                if (!["show", "hide"].includes(settings.dig_target))
                    settings.dig_target = InfoPanel.DEFAULT.dig_target;
                if (!["show", "hide"].includes(settings.talk_target))
                    settings.talk_target = InfoPanel.DEFAULT.talk_target;
                if (!["show", "hide"].includes(settings.search_target))
                    settings.search_target = InfoPanel.DEFAULT.search_target;
                if (!["show", "hide"].includes(settings.search_key))
                    settings.search_key = InfoPanel.DEFAULT.search_key;
                if (!["show", "hide"].includes(settings.hidey_hole))
                    settings.hidey_hole = InfoPanel.DEFAULT.hidey_hole;
                if (!["show", "hide"].includes(settings.emote_items))
                    settings.emote_items = InfoPanel.DEFAULT.emote_items;
                if (!["show", "hide"].includes(settings.emotes))
                    settings.emotes = InfoPanel.DEFAULT.emotes;
                if (!["show", "hide"].includes(settings.double_agent))
                    settings.double_agent = InfoPanel.DEFAULT.double_agent;
                if (!["show", "hide"].includes(settings.path_step_list))
                    settings.path_step_list = InfoPanel.DEFAULT.path_step_list;
                if (!["show", "hide"].includes(settings.puzzle))
                    settings.puzzle = InfoPanel.DEFAULT.puzzle;
                if (!["full", "answer_only", "hide"].includes(settings.challenge))
                    settings.challenge = InfoPanel.DEFAULT.challenge;
                return settings;
            }
            InfoPanel.normalize = normalize;
        })(InfoPanel = Settings.InfoPanel || (Settings.InfoPanel = {}));
        Settings.GeneralSettings = SC.object({
            global_max_zoom: SC.int(0, 7, 7),
            minimum_view_size: SC.int(1, 64, 8),
            include_closest_teleport: SC.boolean(true),
        });
        Settings.normalize = SettingsNormalization.normaliz({
            general: i => Settings.GeneralSettings.parse(i),
            info_panel: InfoPanel.normalize,
            puzzles: Puzzles.normalize,
            compass: CompassSolving.Settings.normalize,
            scans: ScanSolving.Settings.normalize
        });
    })(Settings = ClueSolving.Settings || (ClueSolving.Settings = {}));
})(ClueSolving || (ClueSolving = {}));
